import { ThemeConfig } from '../types/theme';
// Generated themes inherit NEUTRAL light/dark bases (backgrounds, text,
// borders, semantics) so a brand's own hue isn't dragged toward the platform
// default's tint. These are the neutral MAIE Brand / Indigo Night palettes,
// aliased to the local names — deliberately NOT the (tinted) Periwinkle
// platform default.
import {
  maeiBrandTheme as defaultTheme,
  indigoNightTheme as darkTheme,
} from '../config/themes';

// Deterministic palette generation from one or two brand seed colours.
// Used by the AI theme recommender (which only has to choose good seed
// colours — the structural palette is built here, so the model can never
// emit an invalid hex scale) and any other "seed → theme" flow.
//
// Light + dark are generated as a matched pair: same primary/accent hues,
// neutrals/fonts/radii/shadows inherited from the platform light/dark
// bases so contrast and readability stay guaranteed.

type Rgb = { r: number; g: number; b: number };

const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));

export const normalizeHex = (input: string): string | null => {
  let h = input.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{3}$/.test(h)) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return `#${h.toLowerCase()}`;
};

const hexToRgb = (hex: string): Rgb => {
  const h = (normalizeHex(hex) ?? '#000000').slice(1);
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};

const rgbToHex = ({ r, g, b }: Rgb): string =>
  `#${[r, g, b].map((v) => clamp(v).toString(16).padStart(2, '0')).join('')}`;

// Linear blend of `hex` toward `target` by `amount` (0..1).
const mix = (hex: string, target: string, amount: number): string => {
  const a = hexToRgb(hex);
  const b = hexToRgb(target);
  return rgbToHex({
    r: a.r + (b.r - a.r) * amount,
    g: a.g + (b.g - a.g) * amount,
    b: a.b + (b.b - a.b) * amount,
  });
};

const WHITE = '#ffffff';
const BLACK = '#000000';

// Build a Tailwind-style 50–900 scale anchored on `base` at 500.
const scaleFrom = (base: string): ThemeConfig['colors']['primary'] => ({
  50: mix(base, WHITE, 0.92),
  100: mix(base, WHITE, 0.84),
  200: mix(base, WHITE, 0.68),
  300: mix(base, WHITE, 0.48),
  400: mix(base, WHITE, 0.24),
  500: normalizeHex(base) ?? base,
  600: mix(base, BLACK, 0.16),
  700: mix(base, BLACK, 0.32),
  800: mix(base, BLACK, 0.48),
  900: mix(base, BLACK, 0.64),
});

// Relative luminance (sRGB, perceptual-ish) for contrast decisions.
const luminance = (hex: string): number => {
  const { r, g, b } = hexToRgb(hex);
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
};

/** Pick black/white button text for best contrast on a fill. */
export const readableTextOn = (hex: string): string =>
  luminance(hex) > 0.6 ? '#111827' : '#ffffff';

/** True when a hex reads as a dark surface — scheme fallback when a theme
 *  predates the explicit `scheme` field. */
export const isDarkSurface = (hex: string): boolean => luminance(hex) < 0.5;

export type ThemeSurface = 'cool' | 'warm' | 'neutral' | 'deep';

// Dark-UI surface bases by mood. `deep` matches the house Indigo Night base.
const DARK_SURFACE_BASES: Record<
  ThemeSurface,
  { primary: string; secondary: string; tertiary: string }
> = {
  deep: { primary: '#0e1018', secondary: '#181b27', tertiary: '#232838' },
  cool: { primary: '#0f172a', secondary: '#1e293b', tertiary: '#334155' },
  neutral: { primary: '#121214', secondary: '#1d1d20', tertiary: '#2a2a2e' },
  warm: { primary: '#1a1614', secondary: '#241f1c', tertiary: '#322b27' },
};

export interface ThemeSeed {
  /** Brand primary (#rrggbb). */
  primary: string;
  /** Optional accent; defaults to a hue-rotated tint of primary. */
  accent?: string;
  /** Human label shown in the picker / preview. */
  name?: string;
  /** Dark-surface mood (defaults to the house `deep` indigo base). */
  surface?: ThemeSurface;
  /** Brand-tuned semantic colours; fall back to platform defaults. */
  success?: string;
  warning?: string;
  error?: string;
  /** Sign-in hero colours; default to primary/accent. */
  loginPrimary?: string;
  loginAccent?: string;
}

/**
 * Ensure a status/semantic colour stays legible as a FILL with white text
 * (used on chips/badges). If too light, darken until white text has decent
 * contrast; keeps brand-tuned semantics from washing out.
 */
const legibleFill = (hex: string, fallback: string): string => {
  const h = normalizeHex(hex);
  if (!h) return fallback;
  let out = h;
  let guard = 0;
  while (luminance(out) > 0.62 && guard < 6) {
    out = mix(out, BLACK, 0.15);
    guard += 1;
  }
  return out;
};

/**
 * Build a matched light + dark ThemeConfig pair from brand seed colours.
 * The returned light theme carries id `custom` and the dark `custom-dark`,
 * so the existing Branding save path persists them into themeConfig /
 * themeConfigDark without special-casing.
 */
export const buildThemePairFromSeed = (
  seed: ThemeSeed,
): {
  light: ThemeConfig;
  dark: ThemeConfig;
  login: { primary: string; accent: string };
} => {
  const primary = normalizeHex(seed.primary) ?? '#4f46e5';
  // Default accent: shift primary toward a warm complement so secondary
  // surfaces aren't monochrome. Owner-supplied accent always wins.
  const accent = normalizeHex(seed.accent ?? '') ?? mix(primary, '#f59e0b', 0.5);
  const name = seed.name?.trim() || 'AI brand theme';

  // Semantic colours: AI-tuned when provided (kept legible), else platform.
  const successLight = legibleFill(seed.success ?? '', defaultTheme.colors.success);
  const warningLight = legibleFill(seed.warning ?? '', defaultTheme.colors.warning);
  const errorLight = legibleFill(seed.error ?? '', defaultTheme.colors.error);
  // Dark variants nudge slightly brighter so they read on dark surfaces.
  const onDark = (hex: string, fallback: string) =>
    normalizeHex(hex) ? mix(hex, WHITE, 0.1) : fallback;
  const successDark = onDark(seed.success ?? '', darkTheme.colors.success);
  const warningDark = onDark(seed.warning ?? '', darkTheme.colors.warning);
  const errorDark = onDark(seed.error ?? '', darkTheme.colors.error);

  const primaryScale = scaleFrom(primary);
  // Dark UI buttons read better when the primary mid is a touch brighter
  // than the light variant; nudge 500/600 up for the dark scale.
  const darkPrimaryScale: ThemeConfig['colors']['primary'] = {
    ...primaryScale,
    400: mix(primary, WHITE, 0.34),
    500: mix(primary, WHITE, 0.12),
    600: normalizeHex(primary) ?? primary,
  };

  const light: ThemeConfig = {
    id: 'custom',
    scheme: 'light',
    name,
    description: 'Generated from your brand colours',
    colors: {
      primary: primaryScale,
      secondary: primaryScale,
      accent,
      background: { ...defaultTheme.colors.background },
      text: { ...defaultTheme.colors.text },
      border: { ...defaultTheme.colors.border },
      success: successLight,
      warning: warningLight,
      error: errorLight,
      info: primaryScale[600],
    },
    font: { ...defaultTheme.font },
    borderRadius: { ...defaultTheme.borderRadius },
    shadows: { ...defaultTheme.shadows },
  };

  // Dark background base is chosen by the requested surface mood, then given a
  // faint primary tint so the dark theme reads as "branded dark".
  const surfaceBase = DARK_SURFACE_BASES[seed.surface ?? 'deep'];
  const darkBg = {
    primary: mix(surfaceBase.primary, primary, 0.05),
    secondary: mix(surfaceBase.secondary, primary, 0.05),
    tertiary: mix(surfaceBase.tertiary, primary, 0.05),
  };
  // Borders derive from the surface so they stay visible (not the indigo-tuned
  // defaults) regardless of the chosen mood.
  const darkBorder = {
    primary: mix(surfaceBase.tertiary, WHITE, 0.1),
    secondary: mix(surfaceBase.tertiary, WHITE, 0.22),
  };

  const dark: ThemeConfig = {
    id: 'custom-dark',
    scheme: 'dark',
    name: `${name} (dark)`,
    description: 'Generated dark counterpart of your brand theme',
    colors: {
      primary: darkPrimaryScale,
      secondary: darkPrimaryScale,
      accent: mix(accent, WHITE, 0.12),
      background: darkBg,
      text: { ...darkTheme.colors.text },
      border: darkBorder,
      success: successDark,
      warning: warningDark,
      error: errorDark,
      info: darkPrimaryScale[400],
    },
    font: { ...darkTheme.font },
    borderRadius: { ...darkTheme.borderRadius },
    shadows: { ...darkTheme.shadows },
  };

  const login = {
    primary: normalizeHex(seed.loginPrimary ?? '') ?? primary,
    accent: normalizeHex(seed.loginAccent ?? '') ?? accent,
  };

  return { light, dark, login };
};
